import {z} from 'zod';
import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {VerdocsSpinner} from '@verdocs/web-sdk-react';
import {FormProvider, useForm} from 'react-hook-form';
import {IProfile, lockOrganizationMember, VerdocsEndpoint} from '@verdocs/js-sdk';
import {Button, TextInput} from '../Components';
import {Dialog} from './Dialog';

const formSchema = z.object({
  reason: z.string().trim().min(1, 'Please provide a reason.').max(255, 'Reason must be 255 characters or fewer.'),
});

type FormValues = z.infer<typeof formSchema>;

interface DialogProps {
  member: IProfile;
  onClose?: (locked?: boolean) => void;
}

export const LockMemberDialog: FC<DialogProps> = ({member, onClose = () => {}}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(formSchema),
    defaultValues: {reason: ''},
  });

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true);
    try {
      await lockOrganizationMember(VerdocsEndpoint.getDefault(), member.id, data.reason);
      toast.success('Member locked.');
      setSubmitting(false);
      onClose(true);
    } catch (e: any) {
      console.log('Error locking member', e);
      toast.error('Error locking member: ' + e.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog onCancel={() => onClose()}>
      <h1 className="sans-serif font-bold text-xl text-[#33364b] mb-4">
        Lock {`${member.first_name} ${member.last_name}`.trim() || member.email}?
      </h1>
      <p className="text-sm text-gray-600 mb-4">
        They will be unable to sign in until an admin unlocks the account or they complete the password-reset flow.
      </p>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <TextInput name="reason" label="Reason" required placeholder="e.g. Departed employee" />

          <div className="flex flex-row items-center justify-end mt-10 gap-4">
            <Button size="small" variant="secondary" label="Cancel" onClick={() => onClose()} disabled={submitting} />
            <Button size="small" label="Lock" type="submit" disabled={submitting || !form.formState.isValid} />
            {submitting && <VerdocsSpinner mode="dark" />}
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
